/**
 * Exam timetables — which papers a pupil sits, in the order they come, and
 * how long until each one.
 *
 * TWO SOURCES, KEPT APART.
 *
 *   The Cambridge timetable is static reference data: every component of a
 *   series with its date and AM/PM session, extracted from Cambridge's own PDF
 *   by seed/extract-cambridge-timetable.py into src/content/examTimetables/.
 *   It is the same for every centre in the zone (Botswana is Zone 3 — checked
 *   paper by paper against a real statement of entry), so it lives in the repo.
 *
 *   The SERIES (`reportExamSeries`) is the school's part: which classes sit it,
 *   at which level, and which components each subject sits. The office edits
 *   that; nobody edits the Cambridge dates.
 *
 * WHAT A PUPIL SITS COMES FROM THEIR STATEMENT OF ENTRY when it has been
 * imported (`reportExamEntries`, sittingsFromEntries) — exact, paper for paper.
 * Only without one does the subject default apply (Extended papers, the first
 * practical variant). The defaults were the first design, on the reasoning
 * that Core and Extended share a session; the real statements (2026-09-17)
 * proved them wrong for 108 of 113 candidates, because the differences are not
 * only tiers: practical 33 vs 34 on different days, coursework English with no
 * Paper 22, Literature and D&T options, AS Maths routes, and the ICT practicals
 * that sit in a test window.
 *
 * TIMES ARE THE SCHOOL'S SESSIONS, not the paper's length: every pupil is in
 * the exam room for the whole morning (07:30–11:30) or afternoon (12:30–15:30)
 * session, whatever the paper's duration. The duration is shown as information.
 * Everything is Gaborone time (UTC+2, no daylight saving).
 */

/** 'School' is the school's own exams (an internal series). */
export type ExamLevel = 'IGCSE' | 'IGCSE 9-1' | 'O Level' | 'AS' | 'A Level' | 'School';
/* LKC runs THREE sessions a day in its own exams (End of Year 2026: AM 07:30,
   MM 11:00, PM 14:00); Cambridge's timetable has only AM and PM. 'MM' sorts
   between them alphabetically, which every date-then-session sort relies on. */
export type ExamSession = 'AM' | 'MM' | 'PM';

/** What a session is called when the series gives it no name of its own. */
export const SESSION_NAMES: Record<ExamSession, string> = { AM: 'Morning', MM: 'Mid-morning', PM: 'Afternoon' };

/** Al-Nur's own exams call their two sessions "Session 1" and "Session 2"
 *  (both run in the morning there). LKC's Examinations Unit runs three that
 *  really are morning (07:30), mid-morning (11:00) and afternoon (14:00), and
 *  prints them AM / MM / PM — so an LKC internal series uses the plain names. */
export const INTERNAL_SESSION_LABELS: Record<ExamSession, string> = SESSION_NAMES;

/**
 * A session's name on a staff screen. Al-Nur's signature, `(session, internal)`,
 * so its screens port unchanged; a series' own labels may be passed instead.
 */
export function sessionName(
  session: string,
  internalOrLabels: boolean | Partial<Record<ExamSession, string>> = false,
): string {
  const own = typeof internalOrLabels === 'object' ? internalOrLabels?.[session as ExamSession] : undefined;
  if (own) return own;
  return (internalOrLabels === true ? INTERNAL_SESSION_LABELS : SESSION_NAMES)[session as ExamSession] ?? session;
}

/** Days before a child's first paper that the parent's Exams tile comes back
 *  to the home screen (Al-Nur, 2026-10-30). */
export const EXAM_TILE_LEAD_DAYS = 14;

/** Is the child's next paper at most `leadDays` away? Dates YYYY-MM-DD, Gaborone. */
export function examTileDue(dates: string[], today: string, leadDays = EXAM_TILE_LEAD_DAYS): boolean {
  const days = (d: string) => Date.parse(`${d}T00:00:00Z`) / 86400000;
  const next = dates.filter((d) => d >= today).sort()[0];
  return next != null && days(next) - days(today) <= leadDays;
}

/** One component as Cambridge timetables it. */
export interface TimetableComponent {
  level: ExamLevel;
  syllabus: string;
  component: string;
  /** '0625/41' — unique across a whole timetable. */
  code: string;
  /** 'Physics' */
  subject: string;
  /** 'Extended', 'Multiple Choice - Core', '' — the brackets of the title. */
  part: string;
  duration: string;
  minutes: number;
  /** ISO date, '2026-10-07'. */
  date: string;
  session: ExamSession;
}

export interface CambridgeTimetable {
  id: string;
  series: string;
  zone: number;
  source: string;
  extracted: string;
  components: TimetableComponent[];
}

export interface SessionTimes {
  AM: { start: string; end: string };
  /** LKC's mid-morning session; a Cambridge series has none. */
  MM?: { start: string; end: string };
  PM: { start: string; end: string };
}

/** The school's sessions (2026-09-16). */
export const DEFAULT_SESSIONS: SessionTimes = {
  AM: { start: '07:30', end: '11:30' },
  PM: { start: '12:30', end: '15:30' },
};

export interface PaperTime { start: string; end: string }

/** '9702/42' → '9702-42': a map key with no slash, so it can never be read
 *  as a path. */
export function paperTimeKey(code: string): string {
  return String(code || '').replace(/\//g, '-');
}

/**
 * When a paper runs: its own times if the school set them, else its
 * session's. The ONE place that decides, so the pupil's timetable, its PDF
 * and the staff screens cannot disagree about a paper's start.
 */
export function timesForPaper(
  code: string,
  session: ExamSession,
  sessions: SessionTimes = DEFAULT_SESSIONS,
  paperTimes?: Record<string, PaperTime> | null,
): PaperTime {
  const own = paperTimes?.[paperTimeKey(code)];
  const base = sessions[session] ?? DEFAULT_SESSIONS[session] ?? DEFAULT_SESSIONS.AM;
  return {
    start: own?.start || base.start,
    end: own?.end || base.end,
  };
}

/** The levels a class can sit, as offered on the set-up screen. */
export const CLASS_LEVELS: ExamLevel[] = ['IGCSE', 'AS', 'A Level'];

export interface ExamSeriesClass {
  /** The class's DISPLAY name ("Form 5A") — what a pupil's account carries. */
  className: string;
  level: ExamLevel;
}

export interface ExamSubjectEntry {
  /** The portal subject id, exactly as on the pupil record ('physics_'). */
  subjectId: string;
  level: ExamLevel;
  /** Cambridge syllabus code. Empty = this subject sits nothing this series. */
  syllabus: string;
  /** Component numbers sat, e.g. ['21', '41', '51']. */
  components: string[];
}

export interface ExamSeries {
  id: string;
  /** 'internal' is the school's own exams, uploaded on Exams → Internal
   *  exams (lib/internalExams.ts). It has no Cambridge timetable file: every
   *  pupil's papers are on their entries, with their own times. */
  kind: 'cambridge' | 'internal';
  /** What pupils call the two sessions ("First session"); absent = Morning
   *  and Afternoon. */
  sessionLabels?: Partial<Record<ExamSession, string>>;
  title: string;
  /** Which file in src/content/examTimetables/. */
  timetableId: string;
  /** Pupils and parents see a series only once it is published (and the rule
   *  enforces it: they cannot read a draft). */
  published: boolean;
  /** Where every countdown bar starts: the first day of the term. */
  countdownFrom: string;
  sessions: SessionTimes;
  /** A paper's own times where the school runs it outside its session's
   *  (the school, 2026-10-06: the AS papers). Keyed by `paperTimeKey(code)`.
   *  Absent = the session's times. */
  paperTimes?: Record<string, PaperTime>;
  classes: ExamSeriesClass[];
  subjects: ExamSubjectEntry[];
  updatedAt?: unknown;
  updatedBy?: string;
  updatedByName?: string;
}

/* ── subjects and syllabuses ──────────────────────────────────────── */

/** Portal ids drift ('physics_', 'environmental_management_'). */
export function normaliseSubjectId(id: string): string {
  return String(id || '').trim().toLowerCase().replace(/_+$/, '');
}

/** Where A Level components are looked up: an A Level pupil may also sit AS
 *  papers, and an AS/A code is unique within its syllabus. */
function levelFamily(level: ExamLevel): ExamLevel[] {
  return level === 'AS' || level === 'A Level' ? ['AS', 'A Level'] : [level];
}

/**
 * The syllabus the school most likely enters for a portal subject. A starting
 * point for the set-up screen and nothing more — the office confirms each
 * one, and a guess the timetable does not hold is not offered.
 */
// A list is tried in order, first code the timetable holds wins: Business
// Studies 0450 is last sat in November 2026 and Cambridge replaced it with
// IGCSE Business 0264 (first assessed March 2027), so a 2026 timetable
// finds 0450 and every later one 0264.
const SYLLABUS_GUESS: Record<'IGCSE' | 'AS/A', Record<string, string | string[]>> = {
  IGCSE: {
    mathematics: '0580',
    english_language: '0500',
    english_literature: '0475',
    accounting: '0452',
    biology: '0610',
    chemistry: '0620',
    physics: '0625',
    information_and_communitation_technology: '0417',
    information_and_communication_technology: '0417',
    ict: '0417',
    business_studies: ['0264', '0450'],
    business: ['0264', '0450'],
    design_and_technology: '0445',
    environmental_management: '0680',
    computer_science: '0478',
    economics: '0455',
    french: '0520',
    additional_maths: '0606',
    additional_mathematics: '0606',
    history: '0470',
    geography: '0460',
    arabic: '0508',
    setswana: '0698',
  },
  'AS/A': {
    mathematics: '9709',
    physics: '9702',
    chemistry: '9701',
    biology: '9700',
    computer_science: '9618',
    accounting: '9706',
    economics: '9708',
    business: '9609',
    business_studies: '9609',
    english_language: '9093',
  },
};

export function guessSyllabus(subjectId: string, level: ExamLevel, timetable: CambridgeTimetable): string {
  const table = level === 'AS' || level === 'A Level' ? SYLLABUS_GUESS['AS/A'] : SYLLABUS_GUESS.IGCSE;
  const guess = table[normaliseSubjectId(subjectId)] ?? [];
  const codes = Array.isArray(guess) ? guess : [guess];
  return codes.find((code) => code && componentsOf(timetable, level, code).length) ?? '';
}

/** Every component of a syllabus the timetable holds for this level, in paper order. */
export function componentsOf(timetable: CambridgeTimetable, level: ExamLevel, syllabus: string): TimetableComponent[] {
  const family = levelFamily(level);
  return timetable.components
    .filter((c) => c.syllabus === syllabus && family.includes(c.level))
    .sort((a, b) => a.component.localeCompare(b.component));
}

/**
 * The papers entered by default.
 *
 *   IGCSE      Extended rather than Core, and the Practical rather than the
 *              Alternative to Practical — the school's standing entry. Where a
 *              syllabus has no tiers, every component.
 *   AS         The AS components; where Cambridge offers two variants of one
 *              paper (practical 33 and 34, on different days), the first.
 *   A Level    The A Level components only, first variant likewise — the AS
 *              papers were sat the year before.
 *
 * Options within a syllabus (which Mathematics applied paper, which Literature
 * or History option) are not knowable from the timetable, so the office
 * untick what a class does not sit.
 */
export function defaultComponents(timetable: CambridgeTimetable, level: ExamLevel, syllabus: string): string[] {
  let rows = componentsOf(timetable, level, syllabus).filter((c) => c.level === level);
  if (level === 'AS' || level === 'A Level') {
    const seen = new Set<string>();
    rows = rows.filter((c) => {
      const paper = c.component.charAt(0);
      if (seen.has(paper)) return false;
      seen.add(paper);
      return true;
    });
  } else {
    const parts = rows.map((c) => c.part);
    const tiered = parts.some((p) => /\bExtended\b/.test(p));
    const practical = parts.some((p) => /\bPractical\b/.test(p) && !/Alternative to Practical/.test(p));
    rows = rows.filter((c) => {
      if (tiered && /\bCore\b/.test(c.part)) return false;
      if (practical && /Alternative to Practical/.test(c.part)) return false;
      return true;
    });
  }
  return rows.map((c) => c.component);
}

/* ── one pupil's exams ────────────────────────────────────────────── */

export interface PupilExamContext {
  className: string;
  subjectIds: string[];
}

export interface Sitting {
  code: string;
  subjectId: string;
  /** Cambridge's subject name, "Physics". */
  subject: string;
  part: string;
  duration: string;
  minutes: number;
  date: string;
  session: ExamSession;
  /** The school's session, "07:30"–"11:30". */
  start: string;
  end: string;
  /** A TEST-WINDOW paper (the ICT practicals): Cambridge gives a date but no
   *  session, and the school sets the time. Shown as the whole school day. */
  window?: boolean;
  /** Why this pupil's time differs from the paper's (a clash sitting). */
  note?: string;
  /** The series's own name for the session ("First session"). */
  sessionLabel?: string;
}

/* ── a pupil's REAL entries (statement of entry) ──────────────────── */

/** One timed component as Cambridge's statement of entry lists it. */
export interface EntryComponent {
  /** '0625/41' */
  code: string;
  syllabus: string;
  component: string;
  /** "Physics" — the entry's syllabus title. */
  subject: string;
  /** "Theory (Extended)" — the component's own title. */
  title: string;
  /** ISO date. */
  date: string;
  /** '' for a test-window paper. The session the pupil SITS it in, which for
   *  a clash sitting is not Cambridge's (that is kept in `statementSession`). */
  session: ExamSession | '';
  /**
   * A CLASH SITTING (the school, 2026-10-09). A candidate entered for two
   * papers at once sits one of them at another time, in a separate room with
   * its own staff: 9701/22 at 13:00 on 9 Oct for the four who also sit
   * 9701/52 at 11:15. `session` above is moved to the session they sit it in,
   * so registers, seating and session counts follow it unchanged, and these
   * are their own times. Written by seed/apply-clash-sittings.ts, from the
   * rota row marked `sitting: 'clash'` and the statements; carried across a
   * statement re-import by seed/import-statement-of-entry.ts.
   */
  sitting?: { start: string; end: string; dutyId?: string; note?: string };
  /** Cambridge's session for a component moved to a clash sitting. */
  statementSession?: ExamSession | '';
}

/** `reportExamEntries/{seriesId}_{studentId}` — written by
 *  seed/import-statement-of-entry.ts from the centre's CIE Direct statement. */
export interface PupilExamEntries {
  seriesId: string;
  studentId: string;
  candidateNumber: string;
  candidateName: string;
  components: EntryComponent[];
  importedAt: string;
  source: string;
}

/**
 * The pupil's papers from their STATEMENT OF ENTRY — exact, paper for paper.
 *
 * Why this exists: the subject defaults (Extended papers, the first practical
 * variant) were wrong for 108 of 113 candidates when the real statements came
 * in on 2026-09-17 — Core maths, coursework English, practical 34 not 33,
 * Literature and D&T options, test-window ICT practicals. Only the statement
 * knows which papers a pupil actually sits. Dates come from the statement too;
 * the Zone 3 timetable agreed with it on every paper, and supplies the tidier
 * title and the paper length where it has the component.
 */
export function sittingsFromEntries(
  entries: EntryComponent[],
  timetable: CambridgeTimetable,
  sessions: SessionTimes = DEFAULT_SESSIONS,
  paperTimes?: Record<string, PaperTime> | null,
): Sitting[] {
  const byCode = new Map(timetable.components.map((c) => [c.code, c]));
  const out = new Map<string, Sitting>();
  for (const e of entries) {
    if (!e.date || out.has(e.code)) continue;
    const row = byCode.get(e.code);
    const window = !e.session;
    const session: ExamSession = e.session || 'AM';
    const times = e.sitting?.start ? { start: e.sitting.start, end: e.sitting.end } : timesForPaper(e.code, session, sessions, paperTimes);
    out.set(e.code, {
      code: e.code,
      subjectId: '',
      subject: row?.subject ?? e.subject,
      part: row?.part ?? e.title.replace(/\s*\(?\b\d{2}\)?\s*$/, '').trim(),
      duration: row?.duration ?? '',
      minutes: row?.minutes ?? 0,
      date: e.date,
      session,
      /* A TEST-WINDOW PAPER STILL HAS A TIME TO ARRIVE (the school,
         2026-09-19). The ICT practicals carry no session on the statement,
         and the timetable used to answer "the school will tell you" — but
         the school runs them inside an ordinary session, and the candidate
         is expected at its start whatever order the groups are taken in.
         So it reads 07:30 like everything else that morning, and `window`
         now means only "the arrangements will be explained", not "unknown". */
      start: times.start,
      end: times.end,
      ...(window ? { window: true } : {}),
      ...(e.sitting?.note ? { note: e.sitting.note } : {}),
    });
  }
  return [...out.values()].sort(compareSittings);
}

export function levelForClass(series: ExamSeries, className: string): ExamLevel | null {
  const want = String(className || '').trim().toLowerCase();
  if (!want) return null;
  return series.classes.find((c) => c.className.trim().toLowerCase() === want)?.level ?? null;
}

/** Morning before afternoon, then by code — the order a pupil meets them. */
export function compareSittings(a: Pick<Sitting, 'date' | 'session' | 'code'>, b: Pick<Sitting, 'date' | 'session' | 'code'>): number {
  return a.date.localeCompare(b.date) || a.session.localeCompare(b.session) || a.code.localeCompare(b.code);
}

/**
 * The papers this pupil sits in this series, sorted by date and session. A
 * pupil whose class is not in the series sits nothing; a subject the pupil
 * does not take is not theirs, even when their class has an entry for it.
 */
export function sittingsFor(
  series: ExamSeries,
  timetable: CambridgeTimetable,
  pupil: PupilExamContext,
  /** The pupil's statement of entry for this series, when imported: it wins. */
  entries?: EntryComponent[] | null,
): Sitting[] {
  const level = levelForClass(series, pupil.className);
  if (!level) return [];
  if (entries && entries.length) return sittingsFromEntries(entries, timetable, series.sessions ?? DEFAULT_SESSIONS, series.paperTimes);
  const mine = new Set(pupil.subjectIds.map(normaliseSubjectId));
  const byCode = new Map(timetable.components.map((c) => [c.code, c]));
  const family = levelFamily(level);
  const sessions = series.sessions ?? DEFAULT_SESSIONS;

  const out = new Map<string, Sitting>();
  for (const entry of series.subjects) {
    if (entry.level !== level || !entry.syllabus || !mine.has(normaliseSubjectId(entry.subjectId))) continue;
    for (const component of entry.components) {
      const row = byCode.get(`${entry.syllabus}/${component}`);
      if (!row || !family.includes(row.level) || out.has(row.code)) continue;
      const times = timesForPaper(row.code, row.session, sessions, series.paperTimes);
      out.set(row.code, {
        code: row.code,
        subjectId: entry.subjectId,
        subject: row.subject,
        part: row.part,
        duration: row.duration,
        minutes: row.minutes,
        date: row.date,
        session: row.session,
        start: times.start,
        end: times.end,
      });
    }
  }
  return [...out.values()].sort(compareSittings);
}

/* ── countdowns ───────────────────────────────────────────────────── */

const GABORONE_OFFSET_HOURS = 2;

/** The instant a Gaborone wall-clock time happens. */
export function gaboroneInstant(isoDate: string, hhmm = '00:00'): number {
  const [y, m, d] = isoDate.slice(0, 10).split('-').map(Number);
  const [hh, mm] = hhmm.split(':').map(Number);
  return Date.UTC(y, m - 1, d, (hh || 0) - GABORONE_OFFSET_HOURS, mm || 0);
}

/** Calendar days from today in Gaborone to the date — not elapsed hours, so
 *  "tomorrow" does not become "today" at teatime. */
export function calendarDaysUntil(isoDate: string, nowMs: number): number {
  const local = new Date(nowMs + GABORONE_OFFSET_HOURS * 3_600_000);
  const today = Date.UTC(local.getUTCFullYear(), local.getUTCMonth(), local.getUTCDate());
  const [y, m, d] = isoDate.slice(0, 10).split('-').map(Number);
  return Math.round((Date.UTC(y, m - 1, d) - today) / 86_400_000);
}

export type CountdownState = 'upcoming' | 'today' | 'inSession' | 'done';
export type Urgency = 'calm' | 'soon' | 'imminent' | 'none';

export interface Countdown {
  state: CountdownState;
  /** Calendar days to go; 0 on the day, negative once past. */
  days: number;
  /** 0 at the start of the countdown, 1 when the session starts. */
  fraction: number;
  urgency: Urgency;
}

export function countdownFor(sitting: Pick<Sitting, 'date' | 'start' | 'end'>, nowMs: number, countdownFrom: string): Countdown {
  const startMs = gaboroneInstant(sitting.date, sitting.start);
  const endMs = gaboroneInstant(sitting.date, sitting.end);
  const days = calendarDaysUntil(sitting.date, nowMs);
  const fromMs = gaboroneInstant(countdownFrom || sitting.date);
  const span = startMs - fromMs;
  const fraction = span > 0 ? Math.min(1, Math.max(0, (nowMs - fromMs) / span)) : (nowMs >= startMs ? 1 : 0);

  let state: CountdownState = 'upcoming';
  if (nowMs >= endMs) state = 'done';
  else if (nowMs >= startMs) state = 'inSession';
  else if (days <= 0) state = 'today';

  const urgency: Urgency = state === 'done' ? 'none'
    : days <= 2 ? 'imminent'
      : days <= 7 ? 'soon'
        : 'calm';
  return { state, days, fraction, urgency };
}

/** "Today", "Tomorrow", "In 12 days". */
export function daysLabel(c: Countdown): string {
  if (c.state === 'done') return 'Done';
  if (c.state === 'inSession') return 'In session now';
  if (c.days <= 0) return 'Today';
  if (c.days === 1) return 'Tomorrow';
  return `In ${c.days} days`;
}

/** The first paper not yet finished. */
export function nextSitting<T extends Pick<Sitting, 'date' | 'start' | 'end'>>(sittings: T[], nowMs: number): T | null {
  return sittings.find((s) => nowMs < gaboroneInstant(s.date, s.end)) ?? null;
}

export interface ExamDay<T extends Sitting = Sitting> {
  date: string;
  sittings: T[];
}

/** Consecutive papers on one day grouped, keeping the sorted order. */
export function groupByDay<T extends Sitting>(sittings: T[]): ExamDay<T>[] {
  const days: ExamDay<T>[] = [];
  for (const s of sittings) {
    const last = days[days.length - 1];
    if (last && last.date === s.date) last.sittings.push(s);
    else days.push({ date: s.date, sittings: [s] });
  }
  return days;
}

/* ── checks for the set-up screen ─────────────────────────────────── */

export interface SeriesGap {
  kind: 'noEntry' | 'noSyllabus' | 'unknownSyllabus' | 'noComponents';
  level: ExamLevel;
  subjectId: string;
  /** How many pupils in the series' classes take the subject. */
  pupils: number;
}

/**
 * What would leave a pupil with a missing exam: a subject pupils take with no
 * entry, an entry with no syllabus chosen (fine only if the subject really
 * sits nothing — the screen says so rather than refusing), a syllabus the
 * timetable does not hold, or a syllabus with every paper unticked.
 */
export function seriesGaps(series: ExamSeries, timetable: CambridgeTimetable, pupils: PupilExamContext[]): SeriesGap[] {
  const counts = new Map<string, number>();
  for (const p of pupils) {
    const level = levelForClass(series, p.className);
    if (!level) continue;
    for (const s of new Set(p.subjectIds.map(normaliseSubjectId))) {
      const k = `${level}|${s}`;
      counts.set(k, (counts.get(k) ?? 0) + 1);
    }
  }
  const gaps: SeriesGap[] = [];
  const entries = new Map(series.subjects.map((e) => [`${e.level}|${normaliseSubjectId(e.subjectId)}`, e]));
  for (const [k, n] of counts) {
    const [level, subjectId] = k.split('|') as [ExamLevel, string];
    const e = entries.get(k);
    if (!e) gaps.push({ kind: 'noEntry', level, subjectId, pupils: n });
    else if (!e.syllabus) gaps.push({ kind: 'noSyllabus', level, subjectId: e.subjectId, pupils: n });
    else if (!componentsOf(timetable, level, e.syllabus).length) gaps.push({ kind: 'unknownSyllabus', level, subjectId: e.subjectId, pupils: n });
    else if (!e.components.length) gaps.push({ kind: 'noComponents', level, subjectId: e.subjectId, pupils: n });
  }
  return gaps.sort((a, b) => b.pupils - a.pupils || a.subjectId.localeCompare(b.subjectId));
}

/** A new series' subject rows, from the subjects pupils in its classes take. */
export function draftSubjects(
  classes: ExamSeriesClass[],
  timetable: CambridgeTimetable,
  pupils: PupilExamContext[],
): ExamSubjectEntry[] {
  const seen = new Map<string, ExamSubjectEntry>();
  const levels = new Map(classes.map((c) => [c.className.trim().toLowerCase(), c.level]));
  for (const p of pupils) {
    const level = levels.get(p.className.trim().toLowerCase());
    if (!level) continue;
    for (const subjectId of p.subjectIds) {
      const k = `${level}|${normaliseSubjectId(subjectId)}`;
      if (seen.has(k)) continue;
      const syllabus = guessSyllabus(subjectId, level, timetable);
      seen.set(k, {
        subjectId,
        level,
        syllabus,
        components: syllabus ? defaultComponents(timetable, level, syllabus) : [],
      });
    }
  }
  return [...seen.values()].sort((a, b) => a.level.localeCompare(b.level) || a.subjectId.localeCompare(b.subjectId));
}

/* ── the school's exam DAYS, for the people running them ──────────── */

/**
 * One paper as the school meets it: how many of OUR candidates sit it.
 *
 * A pupil's own timetable answers "what do I sit"; running the exam asks the
 * opposite question — who is in the building on Wednesday morning, and how
 * many desks does each paper need. Same entries, counted the other way round.
 */
export interface ExamPaperLoad {
  /** '0625/41' */
  code: string;
  subject: string;
  title: string;
  minutes: number;
  candidates: number;
  /** Classes with at least one candidate, in the school's own order. */
  classes: string[];
  /** When this paper runs ('' in a test window): the session's times unless
   *  the school set the paper its own. */
  start: string;
  end: string;
  /** True when the school set this paper's times itself. */
  ownTimes?: boolean;
}

export interface ExamSessionLoad {
  /** `${date}|${session}` — stable, and what a duty is keyed by. */
  key: string;
  date: string;
  /** '' for a test-window paper, which the school times itself. */
  session: ExamSession | '';
  start: string;
  end: string;
  window: boolean;
  papers: ExamPaperLoad[];
  /** Candidates in the building, counted ONCE however many papers they sit. */
  candidates: number;
}

export interface CandidateEntry {
  studentId: string;
  candidateName: string;
  candidateNumber: string;
  className: string;
  components: EntryComponent[];
}

/**
 * Every session of a series, in order, with the load on each.
 *
 * Built from the STATEMENTS OF ENTRY rather than the subject lists, for the
 * reason recorded on sittingsFromEntries: the defaults were wrong for 108 of
 * 113 candidates. A pupil sitting two papers in one session is one body in one
 * room, so `candidates` counts pupils and `papers[].candidates` counts scripts.
 */
export function sessionLoads(
  candidates: CandidateEntry[],
  timetable: CambridgeTimetable | null,
  sessions: SessionTimes = DEFAULT_SESSIONS,
  paperTimes?: Record<string, PaperTime> | null,
): ExamSessionLoad[] {
  const byCode = new Map((timetable?.components ?? []).map((c) => [c.code, c]));
  const out = new Map<string, ExamSessionLoad & { pupils: Set<string> }>();

  for (const cand of candidates) {
    for (const entry of cand.components) {
      if (!entry.date) continue;
      const key = `${entry.date}|${entry.session}`;
      let load = out.get(key);
      if (!load) {
        load = {
          key,
          date: entry.date,
          session: entry.session,
          // Filled from its papers below: earliest start, latest end.
          start: '',
          end: '',
          window: !entry.session,
          papers: [],
          candidates: 0,
          pupils: new Set<string>(),
        };
        out.set(key, load);
      }
      load.pupils.add(cand.studentId);
      const row = byCode.get(entry.code);
      let paper = load.papers.find((p) => p.code === entry.code);
      if (!paper) {
        const own = entry.sitting?.start ? entry.sitting : paperTimes?.[paperTimeKey(entry.code)];
        const t = entry.sitting?.start ? entry.sitting
          : entry.session ? timesForPaper(entry.code, entry.session, sessions, paperTimes) : null;
        paper = {
          code: entry.code,
          subject: entry.subject || row?.subject || entry.syllabus,
          title: entry.title || row?.part || '',
          minutes: row?.minutes ?? 0,
          candidates: 0,
          classes: [],
          start: t?.start ?? '',
          end: t?.end ?? '',
          ...(entry.session && own && (own.start || own.end) ? { ownTimes: true } : {}),
        };
        load.papers.push(paper);
        /* The session runs from its earliest paper's start to its latest
           paper's end, so a paper given its own times moves the session's
           edges with it. With no own times this is just the session's. */
        if (paper.start && (!load.start || paper.start < load.start)) load.start = paper.start;
        if (paper.end && (!load.end || paper.end > load.end)) load.end = paper.end;
      }
      paper.candidates += 1;
      if (cand.className && !paper.classes.includes(cand.className)) paper.classes.push(cand.className);
    }
  }

  const loads = [...out.values()].map(({ pupils, ...load }) => ({
    ...load,
    candidates: pupils.size,
    papers: [...load.papers]
      .sort((a, b) => b.candidates - a.candidates || a.code.localeCompare(b.code))
      .map((p) => ({ ...p, classes: [...p.classes].sort(compareClassNames) })),
  }));

  // Date first, then the morning before the afternoon; a test window has no
  // session and sits last on its day, because the school chooses its time.
  const rank = (s: ExamSession | '') => (s === 'AM' ? 0 : s === 'MM' ? 1 : s === 'PM' ? 2 : 3);
  return loads.sort((a, b) => a.date.localeCompare(b.date) || rank(a.session) - rank(b.session));
}

/** Form 1 … Form 5, then AS, then A2 — the school's own order. */
export function compareClassNames(a: string, b: string): number {
  const rank = (name: string) => {
    if (/^AS\b/i.test(name)) return 90;
    if (/^A2\b/i.test(name)) return 91;
    const n = Number(/(\d+)/.exec(name)?.[1] ?? 99);
    return Number.isFinite(n) ? n : 99;
  };
  return rank(a) - rank(b) || a.localeCompare(b, undefined, { numeric: true });
}

/** The next session that has not finished, for "what is coming". */
export function nextSessionLoad(loads: ExamSessionLoad[], nowMs: number): ExamSessionLoad | null {
  for (const load of loads) {
    const endsAt = gaboroneInstant(load.date, load.window ? '23:59' : load.end || '23:59');
    if (endsAt >= nowMs) return load;
  }
  return null;
}
